import * as React from 'react';
import Modal from 'react-modal';

import { AdminUploadedFileDetailModal } from 'admin/components/AdminUploadedFileDetail';
import {
  ComposableTable,
  ColumnGeneratorFunc,
  getErrorTextCellGenerator,
  getErrorCellGenerator,
  getTimestampCellGenerator,
  getLinkCellGenerator,
} from 'components/ComposableTable';
import Icon from 'components/Icon';
import Heading from 'components/Heading';
import Row from 'components/Row';
import VisibilityWatch from 'components/VisibilityWatch';
import Text from 'components/Text';

import {
  AdminUploadedFiles_organizations,
  AdminUploadedFiles_organizations_autoMessageFileUploads_nodes,
} from './queries/__generated__/AdminUploadedFiles';

export type AutoMessageFileUpload = AdminUploadedFiles_organizations_autoMessageFileUploads_nodes;
type AutoMessageFileUploadRow = Partial<
  AdminUploadedFiles_organizations_autoMessageFileUploads_nodes
> & { numberOfErrors: number; onClick?: () => void };

type Props = {
  organization?: AdminUploadedFiles_organizations;
  renderLoading?: React.ReactElement;
  renderPaginatedLoading?: React.ReactElement;
  renderError?: React.ReactElement;
  loadNextPage: () => void;
};

type State = {
  uploadedFileForModal?: AutoMessageFileUpload;
  uploadedFileModalIsOpen: boolean;
};

export default class AdminUploadedFilesView extends React.Component<Props, State> {
  state: State = {
    uploadedFileForModal: undefined,
    uploadedFileModalIsOpen: false,
  };

  showUploadedFileModal = (uploadedFile: AutoMessageFileUpload) => {
    this.setState({ uploadedFileForModal: uploadedFile, uploadedFileModalIsOpen: true });
  };

  hideUploadedFileModal = () => {
    this.setState({ uploadedFileForModal: undefined, uploadedFileModalIsOpen: false });
  };

  formatDataIntoRow = (fileUpload: AutoMessageFileUpload): AutoMessageFileUploadRow => {
    const { fileUrl, fileName, errors, uploadedAt, uuid } = fileUpload;
    const numberOfErrors = errors ? errors.length : 0;
    return {
      uuid,
      numberOfErrors,
      fileName,
      fileUrl,
      uploadedAt,
      onClick: numberOfErrors > 0 ? () => this.showUploadedFileModal(fileUpload) : undefined,
    };
  };

  render() {
    const { renderLoading, renderPaginatedLoading, renderError, organization } = this.props;
    const { uploadedFileModalIsOpen, uploadedFileForModal } = this.state;

    if (renderError) {
      return renderError;
    }

    if (renderLoading) {
      return renderLoading;
    }

    const columnHeaders = ['CSV', 'Errors', 'Time received', ''].map(name => ({ name }));
    const columnGenerators: ColumnGeneratorFunc<AutoMessageFileUploadRow>[] = [
      getErrorTextCellGenerator('fileName', 'numberOfErrors', {
        truncate: true,
      }),
      getErrorCellGenerator('numberOfErrors'),
      getTimestampCellGenerator('uploadedAt'),
      getLinkCellGenerator<AutoMessageFileUploadRow>(
        'fileUrl',
        'href',
        <Icon size="medium" name="arrow-down-bar" />
      ),
    ];
    const rows =
      organization && organization.autoMessageFileUploads
        ? organization.autoMessageFileUploads.nodes.map(node => this.formatDataIntoRow(node))
        : [];

    return (
      <React.Fragment>
        {uploadedFileForModal && (
          <Modal isOpen={uploadedFileModalIsOpen}>
            <AdminUploadedFileDetailModal
              onClose={this.hideUploadedFileModal}
              fileUpload={uploadedFileForModal}
            />
          </Modal>
        )}
        <Heading>Uploaded files</Heading>
        <Row size="small">
          <Text size="small" color="graphite">
            CSVs only available for download 90 days after time received.
          </Text>
        </Row>
        <Row>
          <ComposableTable
            columnHeaders={columnHeaders}
            columnGenerators={columnGenerators}
            rows={rows}
            keyField="uuid"
            actionsColumn
          />
        </Row>
        <VisibilityWatch interval={500} whileVisible={this.props.loadNextPage} />
        {renderPaginatedLoading}
        {rows.length === 0 && (
          <Text align="center" element="div" size="small">
            <Row size="small">
              <Text weight="semibold">No files have been received yet</Text>
            </Row>
            <Text color="graphite">
              Files will appear here once they’re transferred to Remind via SFTP
            </Text>
          </Text>
        )}
      </React.Fragment>
    );
  }
}
